import { useEffect, useRef, useState, useCallback } from 'react';
import { Download, Share, X, Plus, Home, Sparkles, MoreVertical, AlertCircle, ExternalLink, Crown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import SafeImage from './SafeImage';

/**
 * PWA install prompt — a branded welcome modal that handles every major
 * mobile browser, with a dedicated fix-path for iOS which has no
 * beforeinstallprompt event and several in-app-browser gotchas.
 *
 * Exposes two entry points:
 *   1. `window.__vlOpenInstall()` — opens the helper modal with
 *      platform-specific install instructions.
 *   2. `window.__vlTriggerInstall()` — fires the native browser install
 *      prompt immediately when available, otherwise falls back to the
 *      helper modal.
 *
 * The iOS path is the tricky one:
 *   • Safari is the only iOS browser that can install PWAs. Chrome/Firefox
 *     on iOS are WebKit wrappers and can't.
 *   • Instagram / Facebook / TikTok in-app browsers cannot install either
 *     — we detect those and show "Open in Safari" instead of Share steps.
 *   • `window.navigator.standalone === true` means the user already launched
 *     from home screen → suppress the modal.
 */
const DISMISS_KEY = 'vl_install_dismissed_at';
const SEEN_KEY = 'vl_install_seen_at';
const INSTALLED_KEY = 'vl_install_installed_at';
const INSTALL_SW_URL = '/sw.js';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  if (document.referrer && document.referrer.startsWith('android-app://')) return true;
  return false;
}

function isNativePlayerApp() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.AndroidPlayerApp || window.TeamLobbyAndroid);
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = (navigator.userAgent || '').toLowerCase();
  const iOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
  if (iOS) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/macintosh|windows|linux/.test(ua)) return 'desktop';
  return 'other';
}

/**
 * iOS-only sub-detector: is the page running inside a webview that
 * CAN'T install (Instagram / FB / TikTok / iOS Chrome / Firefox etc.)?
 * Only real Safari on iOS can install a PWA.
 */
function iosEnvironment() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const lower = ua.toLowerCase();
  if (!/iphone|ipad|ipod/.test(lower) || window.MSStream) return 'other';
  if (/crios|fxios|edgios|opios/.test(lower)) return 'ios-other-browser'; // Chrome/FF/Edge/Opera on iOS
  if (/instagram|fbav|fb_iab|tiktok|line|twitter|snapchat/.test(lower)) return 'ios-in-app';
  // Real Safari identifies as "Safari" and NOT any of the above
  if (/safari/.test(lower)) return 'ios-safari';
  return 'ios-webview';
}

function writeTs(key) {
  try { localStorage.setItem(key, String(Date.now())); } catch { /* noop */ }
}

function hasInstalledMarker() {
  try { return Boolean(localStorage.getItem(INSTALLED_KEY)); } catch { return false; }
}

function markInstalled() {
  writeTs(INSTALLED_KEY);
  try { window.dispatchEvent(new Event('vl:pwa-installed')); } catch { /* noop */ }
}

function clearDismiss() {
  try { localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
}

function canRegisterInstallServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
  return window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

async function registerInstallServiceWorker() {
  if (!canRegisterInstallServiceWorker()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/');
    return existing || await navigator.serviceWorker.register(INSTALL_SW_URL, { scope: '/' });
  } catch {
    return null;
  }
}

async function recordEvent(outcome, platform) {
  try {
    await api.post('/api/pwa/install-event', {
      outcome,
      platform_hint: platform || detectPlatform(),
    });
  } catch { /* pre-auth or offline — no-op */ }
}

// Expose a bridge the rest of the app can call to reopen this modal.
// Set on mount, cleared on unmount.
export function openInstallPrompt(options = {}) {
  if (typeof window !== 'undefined' && typeof window.__vlTriggerInstall === 'function') {
    return window.__vlTriggerInstall(options);
  }
  return false;
}

/**
 * Whether we can even *show* an install affordance on this device.
 * Used by SettingsTab to decide between "Install App" button and a
 * "You're good — already installed" confirmation.
 */
export function canOfferInstall() {
  if (typeof window === 'undefined') return false;
  if (isNativePlayerApp()) return false;
  if (isStandalone()) return false;
  if (hasInstalledMarker()) return false;
  return true;
}

export function InstallAppNudge({ brandLogo, brandName, title, bio, className = '' }) {
  // This check is synchronous in the browser. Starting at false caused the
  // nudge to be inserted one effect later and visibly shift the rewards page.
  const [visible, setVisible] = useState(() => canOfferInstall());

  useEffect(() => {
    const refresh = () => setVisible(canOfferInstall());
    refresh();

    window.addEventListener('appinstalled', refresh);
    window.addEventListener('vl:pwa-installed', refresh);
    const displayMode = window.matchMedia?.('(display-mode: standalone)');
    displayMode?.addEventListener?.('change', refresh);
    displayMode?.addListener?.(refresh);

    return () => {
      window.removeEventListener('appinstalled', refresh);
      window.removeEventListener('vl:pwa-installed', refresh);
      displayMode?.removeEventListener?.('change', refresh);
      displayMode?.removeListener?.(refresh);
    };
  }, []);

  if (!visible) return null;

  const handleInstall = async () => {
    try { navigator.vibrate?.(10); } catch { /* noop */ }
    const opened = await openInstallPrompt({ nativeOnly: true });
    if (!opened) {
      toast('Tap the browser install icon', {
        description: 'Chrome is already showing it beside the address bar.',
        duration: 2600,
      });
    }
  };

  return (
    <section
      className={`overflow-hidden rounded-[18px] border px-3 py-2.5 sm:px-3.5 ${className}`}
      style={{
        background: 'linear-gradient(135deg, rgba(0,77,97,0.13), rgba(59,130,246,0.10) 50%, rgba(250,204,21,0.13))',
        borderColor: 'color-mix(in oklab, var(--vl-neon, #004D61) 36%, rgba(255,255,255,0.16))',
        boxShadow: '0 14px 32px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
      data-testid="install-app-nudge"
    >
      <div className="flex items-center gap-2.5">
        <div
          className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl"
          style={{
            background: 'rgba(0,0,0,0.34)',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: 'inset 0 0 16px rgba(255,255,255,0.05)',
          }}
          aria-hidden
        >
          {brandLogo ? (
            <SafeImage
              src={brandLogo}
              alt={brandName || 'Brand'}
              className="h-full w-full rounded-xl object-cover"
              loading="lazy"
              fallback={<Crown className="h-[18px] w-[18px]" style={{ color: 'var(--vl-neon, #004D61)' }} />}
            />
          ) : (
            <Crown className="h-[18px] w-[18px]" style={{ color: 'var(--vl-neon, #004D61)' }} />
          )}
          <span
            className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full"
            style={{
              background: 'linear-gradient(135deg, var(--vl-neon, #004D61), #FACC15)',
              boxShadow: '0 6px 14px rgba(0,77,97,0.22)',
            }}
          >
            <Plus className="h-3 w-3 text-black" strokeWidth={3} />
          </span>
        </div>

        <button
          type="button"
          onClick={handleInstall}
          className="min-w-0 flex-1 text-left"
          data-testid="install-app-nudge-button"
        >
          <p className="text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: 'var(--vl-neon, #004D61)' }}>
            Install app
          </p>
          <p className="display mt-0.5 text-sm font-extrabold leading-tight text-white sm:text-base">
            {title || `Install ${brandName || 'Slots Oceans'}`}
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-white/68 sm:text-xs">
            {bio || 'Bonuses, payment updates, and chat alerts stay one tap away.'}
          </p>
        </button>

        <button
          type="button"
          onClick={handleInstall}
          className="shrink-0 rounded-xl px-3 py-1.5 text-[11px] font-black text-black transition active:scale-[0.97] sm:px-3.5"
          style={{
            background: 'linear-gradient(135deg, var(--vl-neon, #004D61), #FACC15)',
            boxShadow: '0 10px 20px rgba(0,77,97,0.18)',
          }}
          data-testid="install-app-nudge-cta"
        >
          Install
        </button>
      </div>
    </section>
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState('manual'); // 'chrome' | 'ios-safari' | 'ios-other-browser' | 'ios-in-app' | 'manual' | 'desktop'
  const [branding, setBranding] = useState({
    brand_name: 'Slots Oceans',
    logo_url: '/api/tenants/public/slotsoceans/install-icon',
    home_screen: {},
  });
  const platformRef = useRef('other');
  const firedEvent = useRef(false);
  const deferredRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get('/api/branding', {
      params: { _scope: 'install-branding', _ts: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
      .then((response) => {
        if (active && response.data) setBranding(response.data);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const computeMode = useCallback(() => {
    const platform = detectPlatform();
    platformRef.current = platform;
    if (platform === 'ios') return iosEnvironment(); // ios-safari / ios-other-browser / ios-in-app / ios-webview
    if (platform === 'android') return 'manual';
    return 'desktop';
  }, []);

  // Programmatic helper opener for platforms where there is no native
  // browser install event we can trigger.
  const forceOpen = useCallback(() => {
    clearDismiss();
    setMode((prev) => (prev === 'chrome' ? 'chrome' : computeMode()));
    setVisible(true);
  }, [computeMode]);

  const waitForDeferredPrompt = useCallback(async () => {
    if (deferredRef.current) return deferredRef.current;
    await registerInstallServiceWorker();
    if (navigator.serviceWorker?.ready) {
      try { await Promise.race([navigator.serviceWorker.ready, new Promise((resolve) => setTimeout(resolve, 900))]); } catch { /* noop */ }
    }
    if (deferredRef.current) return deferredRef.current;
    await new Promise((resolve) => setTimeout(resolve, 300));
    return deferredRef.current;
  }, []);

  const triggerInstall = useCallback(async (options = {}) => {
    if (isStandalone()) return false;
    clearDismiss();
    const nativeOnly = Boolean(options?.nativeOnly);
    const promptEvent = deferredRef.current || deferred || await waitForDeferredPrompt();
    if (promptEvent) {
      try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice?.outcome === 'accepted') {
          setVisible(false);
          markInstalled();
          writeTs(DISMISS_KEY);
          writeTs(SEEN_KEY);
          if (!firedEvent.current) {
            firedEvent.current = true;
            recordEvent('accepted', platformRef.current);
          }
        } else {
          dismiss('dismissed');
        }
      } catch {
        if (nativeOnly) return false;
        dismiss('dismissed');
      } finally {
        deferredRef.current = null;
        setDeferred(null);
      }
      return true;
    }

    if (nativeOnly) return false;
    forceOpen();
    return true;
  }, [deferred, forceOpen, waitForDeferredPrompt]);

  useEffect(() => {
    window.__vlOpenInstall = (force) => {
      if (force) forceOpen();
      else if (!isStandalone()) setVisible(true);
    };
    window.__vlTriggerInstall = (options) => triggerInstall(options);
    return () => {
      try { delete window.__vlOpenInstall; } catch { /* noop */ }
      try { delete window.__vlTriggerInstall; } catch { /* noop */ }
    };
  }, [forceOpen, triggerInstall]);

  useEffect(() => {
    if (isStandalone()) return;

    // Admin & staff portals don't want the player install prompt shown on
    // every page load — skip it here. Players are the intended audience.
    let isPortalPage = false;
    try {
      const path = window.location.pathname || '';
      if (/^\/(admin|referral-staff|staff-portal|agent-portal|finance|survey)(\/|$)/.test(path)) {
        isPortalPage = true;
      }
    } catch { /* noop */ }
    if (isPortalPage) return;

    const platform = detectPlatform();
    platformRef.current = platform;
    computeMode();
    registerInstallServiceWorker();

    const onBeforeInstall = (e) => {
      // Iter 142.13 fix: ALWAYS capture the deferred prompt, even when
      // the player has dismissed the auto-pop. Without capture, the
      // Settings → "Add to Home Screen" button has nothing to fire and
      // falls back to a manual-instructions modal — which looks broken
      // on Android Chrome where native install IS available.
      e.preventDefault();
      deferredRef.current = e;
      setDeferred(e);
      setMode('chrome');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      deferredRef.current = null;
      setDeferred(null);
      markInstalled();
      writeTs(DISMISS_KEY);
      if (!firedEvent.current) {
        firedEvent.current = true;
        recordEvent('appinstalled', platform);
      }
      try {
        if (isStandalone()) return;
        const manifestHref = document.querySelector('link[rel="manifest"]')?.href;
        const finishRedirect = (startUrl) => {
          const sep = startUrl.includes('?') ? '&' : '?';
          setTimeout(() => window.location.replace(`${startUrl}${sep}launched=1`), 600);
        };
        if (manifestHref) {
          fetch(manifestHref, { credentials: 'omit' })
            .then((r) => r.ok ? r.json() : null)
            .then((m) => finishRedirect(m?.start_url || '/'))
            .catch(() => finishRedirect('/'));
        } else {
          finishRedirect('/');
        }
      } catch { /* noop */ }
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [computeMode]);

  const dismiss = (outcome = 'dismissed') => {
    setVisible(false);
    writeTs(DISMISS_KEY);
    writeTs(SEEN_KEY);
    recordEvent(outcome, platformRef.current);
  };

  const copyLink = async () => {
    const configured = String(branding.home_screen?.share_url || '').trim();
    const shareUrl = /^https?:\/\//i.test(configured) ? configured : window.location.origin;
    try {
      await navigator.clipboard?.writeText(shareUrl);
      recordEvent('copied_link', platformRef.current);
    } catch { /* noop */ }
  };

  const shareLink = async () => {
    const homeScreen = branding.home_screen || {};
    const configured = String(homeScreen.share_url || '').trim();
    const url = /^https?:\/\//i.test(configured) ? configured : window.location.origin;
    if (navigator.share) {
      try {
        await navigator.share({
          title: homeScreen.share_title || homeScreen.app_name || branding.brand_name || 'Slots Oceans',
          text: homeScreen.share_bio || branding.description || '',
          url,
        });
        recordEvent('shared_link', platformRef.current);
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await copyLink();
  };

  if (!visible) return null;

  const isIos = mode.startsWith('ios');
  const homeScreen = branding.home_screen || {};
  const installIcon = homeScreen.icon_url || branding.logo_url || '/api/tenants/public/slotsoceans/install-icon';
  const installName = homeScreen.app_name || branding.brand_name || 'Slots Oceans';
  const installTitle = homeScreen.install_title || `Install ${installName}`;
  const installBio = homeScreen.install_bio || 'Launch faster and keep payment, bonus, and chat updates one tap away.';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/75 backdrop-blur-sm"
      data-testid="pwa-install-welcome"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div
        className="relative w-full md:max-w-sm bg-[#131822] md:border border-[#D4AF37]/30 md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden animate-[slideUp_.35s_ease-out]"
        style={{ paddingBottom: isIos ? 'env(safe-area-inset-bottom)' : undefined }}
      >
        <button
          onClick={() => dismiss('dismissed')}
          className="absolute top-3 right-3 p-2 rounded-full bg-black/40 hover:bg-black/70 text-gray-300 hover:text-white transition z-10"
          aria-label="Close"
          data-testid="pwa-welcome-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header — iOS gets a cleaner, taller hero since the platform relies
            entirely on the visual Share icon, not a programmatic button. */}
        <div
          className={`${isIos ? 'px-6 pt-10 pb-6' : 'px-6 pt-8 pb-5'} text-center`}
          style={{ background: 'radial-gradient(120% 100% at 50% 0%, rgba(212,175,55,0.22), transparent 72%)' }}
        >
          <div
            className={`${isIos ? 'w-24 h-24' : 'w-20 h-20'} rounded-[22px] mx-auto flex items-center justify-center overflow-hidden shadow-lg bg-[#0B0E14]`}
            style={{ boxShadow: '0 10px 30px rgba(212,175,55,0.25)' }}
          >
            <img
              src={installIcon}
              alt={installName}
              className="w-full h-full object-contain p-1.5"
              data-testid="install-prompt-logo"
            />
          </div>
          <h2 className={`${isIos ? 'text-[22px]' : 'text-xl'} font-bold text-white mt-4 flex items-center justify-center gap-2`}>
            {installTitle}
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          </h2>
          <p className={`${isIos ? 'text-[13.5px]' : 'text-[13px]'} text-gray-400 mt-2 leading-relaxed px-1`}>
            {installBio}
          </p>
        </div>

        {/* Body per-platform */}
        <div className="px-5 pb-5 space-y-3">
          {mode === 'ios-safari' && <IosInstructions />}
          {mode === 'ios-other-browser' && <IosOtherBrowserWarning onCopyLink={copyLink} />}
          {(mode === 'ios-in-app' || mode === 'ios-webview') && <IosInAppWarning onCopyLink={copyLink} />}
          {mode === 'manual' && <ManualAndroidInstructions />}
          {mode === 'desktop' && <DesktopInstructions />}
          {mode === 'chrome' && (
            <div className="text-[12px] text-gray-400 bg-[#0B0E14] border border-white/5 rounded-xl p-3 leading-relaxed">
              One tap to install — your browser will confirm. No app store, no downloads.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`${isIos ? 'px-5 pb-6' : 'px-5 pb-5'} flex gap-2`}>
          <button
            onClick={() => dismiss('dismissed')}
            className="flex-1 py-3 rounded-xl text-sm font-medium text-gray-400 hover:bg-white/5 transition"
            data-testid="pwa-welcome-later"
          >
            Not Now
          </button>
          <button
            type="button"
            onClick={shareLink}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/75 transition hover:bg-white/10 hover:text-white active:scale-[0.97]"
            aria-label="Share app link"
            title="Share app link"
            data-testid="pwa-welcome-share"
          >
            <Share className="h-4 w-4" />
          </button>
          {mode === 'chrome' ? (
            <button
              onClick={triggerInstall}
              className="flex-1 py-3 rounded-xl text-sm font-bold text-black transition hover:scale-[1.02] flex items-center justify-center gap-1"
              style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
              data-testid="pwa-welcome-install"
            >
              <Download className="w-4 h-4" /> Install
            </button>
          ) : (
            <button
              onClick={() => dismiss('acknowledged')}
              className="flex-1 py-3 rounded-xl text-sm font-bold text-black transition active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
              data-testid="pwa-welcome-got-it"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, children, icon }) {
  return (
    <div className="flex items-start gap-3 bg-[#0B0E14] border border-white/5 rounded-xl px-3.5 py-3">
      <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center shrink-0">{n}</div>
      <div className="flex-1 text-[13px] text-gray-200 leading-relaxed flex items-center gap-2 flex-wrap">
        {children}
        {icon}
      </div>
    </div>
  );
}

/**
 * iOS Safari: walks through the three real-world taps, using bigger,
 * more-legible steps than the Android fallback because iOS users depend
 * 100% on these instructions (there is no native Install button).
 */
function IosInstructions() {
  return (
    <div className="space-y-2" data-testid="pwa-ios-steps">
      <Step n={1} icon={<Share className="w-4 h-4 text-[#D4AF37]" />}>
        Tap the <b className="text-white">Share</b> button — it's the square-with-arrow icon at the bottom of Safari.
      </Step>
      <Step n={2} icon={<Plus className="w-4 h-4 text-[#D4AF37]" />}>
        Scroll and choose <b className="text-white">Add to Home Screen</b>.
      </Step>
      <Step n={3}>
        Tap <b className="text-white">Add</b> — your lobby lands on the home screen.
      </Step>
    </div>
  );
}

/**
 * iOS Chrome / Firefox / Edge / Opera: these are WebKit wrappers on iOS
 * and can't trigger "Add to Home Screen" — Safari can. We nudge users to
 * reopen in Safari and give them a one-tap copy-link button.
 */
function IosOtherBrowserWarning({ onCopyLink }) {
  return (
    <div className="space-y-2.5" data-testid="pwa-ios-other-browser">
      <div className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(245,204,50,0.08)', border: '1px solid rgba(245,204,50,0.25)' }}>
        <AlertCircle className="w-5 h-5 text-[#F5D76E] shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-gray-200 leading-relaxed">
          On iPhone, only <b className="text-white">Safari</b> can add web apps to your home screen. Your current browser can't.
        </div>
      </div>
      <Step n={1} icon={<ExternalLink className="w-4 h-4 text-[#D4AF37]" />}>
        Open <b className="text-white">Safari</b> on your iPhone.
      </Step>
      <Step n={2}>
        Paste this link:
        <button
          onClick={onCopyLink}
          className="ml-1 px-2 py-0.5 rounded-md bg-[#D4AF37]/15 text-[#D4AF37] text-[11px] font-semibold hover:bg-[#D4AF37]/25"
          data-testid="pwa-copy-link"
        >
          Copy link
        </button>
      </Step>
      <Step n={3} icon={<Share className="w-4 h-4 text-[#D4AF37]" />}>
        In Safari, tap <b className="text-white">Share → Add to Home Screen</b>.
      </Step>
    </div>
  );
}

/**
 * Instagram / Facebook / TikTok / etc. in-app browsers on iOS. These
 * webviews have no Share sheet that can add to home screen. Best UX is
 * a clear "tap ⋯ → Open in Safari" with a copy-link fallback.
 */
function IosInAppWarning({ onCopyLink }) {
  return (
    <div className="space-y-2.5" data-testid="pwa-ios-in-app">
      <div className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
        <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-gray-200 leading-relaxed">
          You're inside an in-app browser. Installing to home screen from here isn't possible — but it takes two taps to reopen in Safari.
        </div>
      </div>
      <Step n={1} icon={<MoreVertical className="w-4 h-4 text-[#D4AF37]" />}>
        Tap the <b className="text-white">⋯</b> menu (top-right of the browser).
      </Step>
      <Step n={2} icon={<ExternalLink className="w-4 h-4 text-[#D4AF37]" />}>
        Choose <b className="text-white">Open in Safari</b> (or similar).
      </Step>
      <Step n={3}>
        Then tap <b className="text-white">Share → Add to Home Screen</b>.
        <button
          onClick={onCopyLink}
          className="ml-1 px-2 py-0.5 rounded-md bg-[#D4AF37]/15 text-[#D4AF37] text-[11px] font-semibold hover:bg-[#D4AF37]/25"
          data-testid="pwa-copy-link-in-app"
        >
          Copy link
        </button>
      </Step>
    </div>
  );
}

function ManualAndroidInstructions() {
  return (
    <div className="space-y-2" data-testid="pwa-manual-steps">
      <Step n={1} icon={<MoreVertical className="w-4 h-4 text-[#D4AF37]" />}>
        Open the browser menu (top-right <b className="text-white">⋮</b> or share icon).
      </Step>
      <Step n={2} icon={<Plus className="w-4 h-4 text-[#D4AF37]" />}>
        Tap <b className="text-white">Install app</b> or <b className="text-white">Add to Home screen</b>.
      </Step>
      <Step n={3}>
        Confirm — launch from your phone's home screen anytime.
      </Step>
    </div>
  );
}

function DesktopInstructions() {
  return (
    <div className="space-y-2" data-testid="pwa-desktop-steps">
      <Step n={1} icon={<Download className="w-4 h-4 text-[#D4AF37]" />}>
        Look for the <b className="text-white">install icon</b> in your browser's address bar.
      </Step>
      <Step n={2}>
        Click it and confirm — a standalone window opens, ready to use.
      </Step>
    </div>
  );
}
